import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BASEMAP_ADDON_GIS } from '@rsos/constants/basemapAddonNames';
import { createLayerFromUrl } from '@rsos/maps/providers/esri/layers/createLayerFromUrl';
import { addBasemapServiceSublayers } from '@rsos/reduxDataStore/src/features/gisServices/gisServicesSlice';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import usePrevious from '@rsos/utils/usePrevious';
import checkLayerVisiblity from '../utils/checkLayerVisiblity';
import { getGISLayerID } from '../utils/getGISLayerID';
import {
  getNestedFeatureLayer,
  getNestedMapImageLayer,
} from '../utils/getNestedLayers';

/**
 * An Esri map GIS basemap layer hook if the agency has GIS basemap service configured.
 * @returns Object
 */
const useGISBasemapLayer = () => {
  const dispatch = useDispatch();

  const hasEsriMapAccess = useSelector(state =>
    checkEsriMapAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const basemapService = useSelector(state => state.gisServices.basemapService);

  const mapAddons = useSelector(state => state.sinatra.ui.mapAddons);

  const [cachedBasemapLayer, setCachedBasemapLayer] = useState(null);
  const [gisBasemapSublayers, setGisBasemapSublayers] = useState([]);
  const previousBasemapSublayersLength = usePrevious(
    gisBasemapSublayers?.length || 0,
  );
  // Create GIS cached-basemap layer and sublayers on load.

  useEffect(() => {
    if (hasEsriMapAccess && Object.keys(basemapService).length) {
      const createBasemapLayers = async () => {
        const layerData = {
          apiKey: basemapService?.token,
          id: getGISLayerID(basemapService),
          url: basemapService.gis_service_url,
          visible: mapAddons.includes(BASEMAP_ADDON_GIS),
          outFields: ['*'],
        };

        const initialPromise = createLayerFromUrl(layerData)
          .then(layer => {
            if (layer?.loaded) {
              const loadedLayer = checkLayerVisiblity(layer);

              setCachedBasemapLayer({
                layer: loadedLayer,
                service: basemapService,
              });

              // Check if layer has sublayers and determine layer type
              const sublayers = layer?.layers || layer?.sublayers || null;

              if (sublayers?.items?.length > 0) {
                if (
                  basemapService?.attributes?.gis_service_category ===
                  'map_service'
                ) {
                  return getNestedMapImageLayer(
                    layer,
                    basemapService,
                    layerData,
                  );
                }
                return getNestedFeatureLayer(layer, basemapService, layerData);
              }
            }
            return null;
          })
          .catch(() => {
            // fail silently
            return null;
          });

        const intermediateResult = await initialPromise;

        if (intermediateResult) {
          const finalLayerPromises = Array.isArray(intermediateResult)
            ? intermediateResult
            : [intermediateResult];

          const resolvedLayerResults = await Promise.all(finalLayerPromises);

          const validLayers = resolvedLayerResults.filter(
            result => result !== null,
          );

          setGisBasemapSublayers(validLayers);
        }
      };

      createBasemapLayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEsriMapAccess, basemapService]);

  useEffect(() => {
    if (gisBasemapSublayers?.length > previousBasemapSublayersLength) {
      const sublayerServices = gisBasemapSublayers.map(layer => {
        return layer.service;
      });
      dispatch(addBasemapServiceSublayers(sublayerServices));
    }
  }, [dispatch, previousBasemapSublayersLength, gisBasemapSublayers?.length]);

  return { cachedBasemapLayer, gisBasemapSublayers };
};

export default useGISBasemapLayer;
